import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  size,
  useFloating,
} from "@floating-ui/react";
import { Popover, Transition } from "@headlessui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentAddIcon,
  FolderAddIcon,
  FolderIcon,
  PlusIcon,
  SearchIcon,
  SparklesIcon,
} from "@heroicons/react/outline";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { RootState } from "src/common/state/store";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { useOpenNewModuleWizard } from "src/features/projectGroups/hooks/useOpenNewModuleWizard";
import {
  fetchSearchIndex,
  fetchSpecificProjectGroupWithProjects,
  getSearchIndex,
} from "src/features/projectGroups/state/projectGroupsSlice";
import Spinner from "../Loader/Spinner";
import { SidebarNavRow } from "./SidebarItem";

const menuRowClassName =
  "flex w-full items-center gap-2 px-4 py-2 text-sm text-black text-left hover:bg-blue-100 focus:outline-none focus-visible:bg-blue-100 disabled:opacity-50";
const menuIconClassName = "w-4 h-4 stroke-gray-500 shrink-0";

type CourseSort = "recent" | "alpha";

const sortPillClassName = (active: boolean) =>
  `text-xs px-2.5 py-0.5 rounded-full border ${
    active
      ? "bg-blue-500 border-blue-500 text-white"
      : "border-gray-300 text-gray-700 hover:bg-blue-50"
  }`;

const normalizeForSearch = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const ModulePicker = ({ onBack, onDone }: { onBack: () => void; onDone: () => void }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const openNewModuleWizard = useOpenNewModuleWizard();
  const searchIndex = useAppSelector(getSearchIndex);
  const searchIndexStatus = useAppSelector(
    (state: RootState) => state.projectGroups.searchIndexStatus,
  );
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<CourseSort>("recent");
  const [isOpening, setIsOpening] = useState(false);

  useEffect(() => {
    if (searchIndexStatus === "idle") {
      void dispatch(fetchSearchIndex({}));
    }
  }, [dispatch, searchIndexStatus]);

  const courses = useMemo(() => {
    const needle = normalizeForSearch(query.trim());
    return searchIndex
      .filter(
        (item) =>
          item.type === "projectGroup" &&
          (needle === "" || normalizeForSearch(item.name).includes(needle)),
      )
      .sort((a, b) => {
        if (sort === "alpha")
          return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
        if (!a.updatedAt) return b.updatedAt ? 1 : 0;
        if (!b.updatedAt) return -1;
        return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
      });
  }, [searchIndex, query, sort]);

  const openCourse = async (projectGroupId: number) => {
    setIsOpening(true);
    try {
      const res = await dispatch(
        fetchSpecificProjectGroupWithProjects({ projectGroupId }),
      ).unwrap();
      openNewModuleWizard(res.data);
      onDone();
    } catch {
      setIsOpening(false);
      dispatch(setAlert({ type: "danger", msg: t("sidebar.createMenu.loadError") }));
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <button
          type="button"
          onClick={onBack}
          aria-label={t("general.back")}
          className="rounded hover:bg-blue-100"
        >
          <ChevronLeftIcon className="w-4 h-4 stroke-gray-500" />
        </button>
        <p className="text-sm font-semibold text-black">{t("sidebar.createMenu.moduleIn")}</p>
      </div>
      <div className="relative flex flex-col min-h-0" aria-busy={isOpening}>
        {isOpening && (
          <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
            <Spinner relative />
          </div>
        )}
        <label className="mx-3 mt-3 mb-2 flex items-center gap-2 border border-gray-300 rounded-md px-2 py-1.5 text-sm">
          <SearchIcon className="w-4 h-4 stroke-gray-400 shrink-0" />
          <input
            autoFocus
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("sidebar.createMenu.search")}
            aria-label={t("sidebar.createMenu.search")}
            className="w-full min-w-0 border-0 bg-transparent p-0 text-sm text-black placeholder-gray-400 focus:ring-0 focus:outline-none"
          />
        </label>
        <div className="flex items-center gap-1.5 px-3 pb-2">
          {(["recent", "alpha"] as const).map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={sort === key}
              onClick={() => setSort(key)}
              className={sortPillClassName(sort === key)}
            >
              {t(
                key === "recent" ? "sidebar.createMenu.sortRecent" : "sidebar.createMenu.sortAlpha",
              )}
            </button>
          ))}
          <span className="ml-auto text-xs text-gray-500">
            {t("sidebar.createMenu.count", { count: courses.length })}
          </span>
        </div>
        {searchIndexStatus === "loading" && courses.length === 0 ? (
          <div className="relative h-16">
            <Spinner />
          </div>
        ) : courses.length === 0 ? (
          <div className="pb-1">
            <p className="px-4 py-2 text-sm text-gray-400">{t("sidebar.createMenu.empty")}</p>
            <Link to="/courses/new" onClick={onDone} className={menuRowClassName}>
              <FolderAddIcon className={menuIconClassName} />
              <span>{t("sidebar.createMenu.parcours")}</span>
            </Link>
          </div>
        ) : (
          <ul className="min-h-0 max-h-72 overflow-y-auto border-t border-gray-100 divide-y divide-blue-50 rounded-b-md">
            {courses.map((course) => (
              <li key={course.id}>
                <button
                  type="button"
                  disabled={isOpening}
                  onClick={() => void openCourse(Number(course.id))}
                  className={menuRowClassName}
                >
                  <FolderIcon className={menuIconClassName} />
                  <span className="truncate">{course.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

const CreateMenuPanelBody = ({
  canUseWixarAi,
  close,
}: {
  canUseWixarAi: boolean;
  close: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState<"root" | "module">("root");

  const goTo = (path: string) => {
    close();
    navigate(path);
  };

  if (view === "module") {
    return <ModulePicker onBack={() => setView("root")} onDone={close} />;
  }

  return (
    <>
      {canUseWixarAi && (
        <div className="p-2">
          <button
            type="button"
            onClick={() => goTo("/chat/scenario-helper")}
            className="relative w-full rounded-lg bg-blue-500 p-4 text-left cursor-pointer overflow-hidden"
          >
            <div className="absolute -right-6 -top-8 w-32 h-32 rounded-full bg-green-400/40 blur-2xl" />
            <div className="absolute -left-8 -bottom-10 w-28 h-28 rounded-full bg-yellow-600/40 blur-2xl" />
            <div className="relative flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-600 to-green-400 shadow-lg shrink-0">
                <SparklesIcon className="w-6 h-6 stroke-white" />
              </span>
              <div className="flex-1">
                <p className="text-white font-bold text-base leading-5">
                  {t("sidebar.createMenu.aiTitle")}
                </p>
                <p className="text-blue-200 text-xs mt-0.5">{t("sidebar.createMenu.aiSubtitle")}</p>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wide text-blue-500 bg-green-400 rounded-full px-2 py-0.5">
                {t("sidebar.createMenu.new")}
              </span>
            </div>
          </button>
        </div>
      )}
      <div className={`py-1 ${canUseWixarAi ? "border-t border-gray-100" : ""}`}>
        <button type="button" onClick={() => goTo("/courses/new")} className={menuRowClassName}>
          <FolderAddIcon className={menuIconClassName} />
          <span>{t("sidebar.createMenu.parcours")}</span>
        </button>
        <button type="button" onClick={() => setView("module")} className={menuRowClassName}>
          <DocumentAddIcon className={menuIconClassName} />
          <span>{t("sidebar.createMenu.module")}</span>
          <ChevronRightIcon className="w-3 h-3 stroke-gray-400 ml-auto" />
        </button>
      </div>
    </>
  );
};

const CreateMenuFlyout = ({
  open,
  close,
  title,
  canUseWixarAi,
}: {
  open: boolean;
  close: () => void;
  title: string;
  canUseWixarAi: boolean;
}) => {
  const { refs, floatingStyles } = useFloating({
    open,
    placement: "right-start",
    middleware: [
      offset(16),
      flip(),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.max(availableHeight, 160)}px`;
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
    transform: false,
  });

  return (
    <>
      <SidebarNavRow
        icon={PlusIcon}
        title={title}
        isSelected={open}
        trailing={<ChevronRightIcon className="w-3 h-3 stroke-gray-400 ml-auto shrink-0" />}
        renderPrimary={(className, content) => (
          <Popover.Button
            ref={(node: HTMLButtonElement | null) => refs.setReference(node)}
            className={className}
          >
            {content}
          </Popover.Button>
        )}
      />
      <FloatingPortal>
        <Transition
          show={open}
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Popover.Panel
            ref={(node: HTMLDivElement | null) => refs.setFloating(node)}
            style={floatingStyles}
            data-testid="create-menu-panel"
            className="z-[60] flex flex-col w-80 origin-top-left bg-white border border-blue-300 rounded-md shadow-2xl shadow-blue-500/30 outline-none"
          >
            <CreateMenuPanelBody canUseWixarAi={canUseWixarAi} close={close} />
          </Popover.Panel>
        </Transition>
      </FloatingPortal>
    </>
  );
};

const SidebarCreateMenu = ({ title, canUseWixarAi }: { title: string; canUseWixarAi: boolean }) => (
  <Popover as="div">
    {(popover) => (
      <CreateMenuFlyout
        open={popover.open}
        close={() => popover.close()}
        title={title}
        canUseWixarAi={canUseWixarAi}
      />
    )}
  </Popover>
);

export default SidebarCreateMenu;
